/**
 * SearchableClientDropdown Component
 * 
 * Searchable dropdown for filtering jobs by client/company name.
 * Features:
 * - Case-insensitive search by client name
 * - Keyboard navigation (Arrow keys, Enter, Escape)
 * - "All Clients" option always visible
 * - Accessible with ARIA attributes
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface SearchableClientDropdownProps {
  clients: string[];
  selectedClient: string | null;
  onChange: (client: string | null) => void;
}

const SearchableClientDropdown: React.FC<SearchableClientDropdownProps> = ({
  clients,
  selectedClient,
  onChange
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter clients based on search query (case-insensitive)
  const filteredClients = searchQuery.trim()
    ? clients.filter(client => 
        client.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : clients;

  // Get selected client name for display
  const selectedClientName = selectedClient || 'All Clients';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (client: string | null) => {
    onChange(client);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    // Total options: "All Clients" + filtered clients
    const totalOptions = filteredClients.length + 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;

      case 'Enter':
        e.preventDefault();
        if (highlightedIndex === 0) {
          // "All Clients" option
          handleSelect(null);
        } else if (highlightedIndex > 0 && highlightedIndex <= filteredClients.length) {
          // Client option
          handleSelect(filteredClients[highlightedIndex - 1]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;

      case 'Tab':
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;

      default:
        // Reset highlight when typing
        if (e.key.length === 1) {
          setHighlightedIndex(0);
        }
        break;
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setHighlightedIndex(-1);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="w-full px-2.5 py-1 text-xs text-left text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Filter by client"
      >
        <span className="truncate">{selectedClientName}</span>
        <ChevronDownIcon 
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search clients..."
                className="w-full pl-9 pr-8 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                aria-label="Search clients"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <ul
            ref={listRef}
            role="listbox"
            className="max-h-60 overflow-y-auto py-1"
            aria-label="Client options"
          >
            {/* "All Clients" Option - Always visible */}
            <li
              role="option"
              aria-selected={selectedClient === null}
              onClick={() => handleSelect(null)}
              onMouseEnter={() => setHighlightedIndex(0)}
              className={`px-4 py-2 text-xs cursor-pointer ${
                highlightedIndex === 0
                  ? 'bg-blue-50 text-blue-700'
                  : selectedClient === null
                  ? 'bg-gray-50 text-gray-900 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              All Clients
            </li>

            {/* Client Options */}
            {filteredClients.length > 0 ? (
              filteredClients.map((client, index) => {
                const optionIndex = index + 1; // +1 because "All Clients" is at index 0
                const isSelected = selectedClient === client;
                const isHighlighted = highlightedIndex === optionIndex;

                return (
                  <li
                    key={client}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(client)}
                    onMouseEnter={() => setHighlightedIndex(optionIndex)}
                    className={`px-4 py-2 text-xs cursor-pointer ${
                      isHighlighted
                        ? 'bg-blue-50 text-blue-700'
                        : isSelected
                        ? 'bg-gray-50 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {client}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-xs text-gray-500 italic">
                No clients found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchableClientDropdown;
